import React, { useEffect, useState } from 'react';
import { PercentCrop } from 'react-image-crop';

type UseDrawCroppedImageToCanvasArgs = {
  imageUrl: string;
  cropInfo: PercentCrop;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  width: number;
  height: number;
};

export const useDrawCroppedImageToCanvas = (args: UseDrawCroppedImageToCanvasArgs) => {
  const { imageUrl, cropInfo, canvasRef, width, height } = args;

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const drawCroppedImage = (imageUrlToDraw: string) => {
      const image = new Image();
      image.crossOrigin = 'anonymous'; // Allow cross-origin images
      image.src = imageUrlToDraw;

      image.onload = () => {
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          if (ctx) {
            try {
              const { x, y, width: cropWidth, height: cropHeight } = cropInfo;

              // Convert percent values to pixels
              const cropX = (x / 100) * image.width;
              const cropY = (y / 100) * image.height;
              const cropWidthPx = (cropWidth / 100) * image.width;
              const cropHeightPx = (cropHeight / 100) * image.height;

              // Set canvas size to match the desired output size
              const devicePixelRatio = window.devicePixelRatio;
              canvas.width = width * devicePixelRatio;
              canvas.height = height * devicePixelRatio;
              ctx.scale(devicePixelRatio, devicePixelRatio);

              ctx.imageSmoothingQuality = 'high';

              // Clear canvas and draw the cropped image
              ctx.clearRect(0, 0, width, height);
              ctx.drawImage(
                image,
                cropX,
                cropY,
                cropWidthPx,
                cropHeightPx, // Source crop
                0,
                0,
                width,
                height, // Destination canvas
              );
              setHasError(false);
            } catch (error) {
              setHasError(true);
              // Fallback to showing the full image
              canvas.width = width;
              canvas.height = height;
              ctx.clearRect(0, 0, width, height);
              ctx.drawImage(image, 0, 0, width, height);
            }
          }
        }
      };

      image.onerror = () => {
        setHasError(true);
      };
    };

    drawCroppedImage(imageUrl);
  }, [cropInfo, width, height, imageUrl, canvasRef]);

  return {
    hasError,
  };
};
